import { Icon, mergeStyles,Spinner, SpinnerSize, Text } from '@fluentui/react';
import * as React from 'react';

import { SubscriptionNode } from '../../../../Common/ConsumerModels/SubcriptionNode';

type ViewAttributeSubscriptionsFormProps = {
  formattedApprovedAttributes: SubscriptionNode[];
  setFormattedApprovedAttributes(approvedAttributes: SubscriptionNode[]): void;
  filteredApprovedAttributes: SubscriptionNode[];
  setFilteredApprovedAttributes(approvedAttributes: SubscriptionNode[]): void;
  isApprovedAttributesLoading: boolean;
};

export function ViewAttributeSubscriptionsForm(props: ViewAttributeSubscriptionsFormProps): React.ReactElement {
  const {
    formattedApprovedAttributes,
    setFormattedApprovedAttributes,
    filteredApprovedAttributes,
    setFilteredApprovedAttributes,
    isApprovedAttributesLoading,
  } = props;

  const dropdownIconClass = mergeStyles({
    fontSize: 20,
    height: 20,
    width: 20,
    color: '#242424',
  });

  const completedCheckIconClass = mergeStyles({
    fontSize: 16,
    height: 16,
    width: 16,
    color: '#0078D4',
    margin: '2px 0px',
  });

  const onChangeDropdown = (tenantNode: SubscriptionNode, filteredTenantIndex: number): void => {

    // Set dropdown for formattedApprovedAttributes tree
    const newFormattedApprovedAttributes: SubscriptionNode[] = JSON.parse(
      JSON.stringify(formattedApprovedAttributes) // deep copy
    );
    const formattedTenantIndex: number = tenantNode.pathFromRoot as unknown as number;
    const formattedDropdown: boolean = newFormattedApprovedAttributes[formattedTenantIndex].isDropdownExpand;
    newFormattedApprovedAttributes[formattedTenantIndex].isDropdownExpand = !formattedDropdown;

    setFormattedApprovedAttributes(newFormattedApprovedAttributes);

    // Set dropdown for filteredAprovedAttributes tree
    const newFilteredApprovedAttributes: SubscriptionNode[] = JSON.parse(
      JSON.stringify(formattedApprovedAttributes) // deep copy
    );
    const filteredDropdown: boolean = newFilteredApprovedAttributes[filteredTenantIndex].isDropdownExpand;
    newFilteredApprovedAttributes[filteredTenantIndex].isDropdownExpand = !filteredDropdown;

    setFilteredApprovedAttributes(newFilteredApprovedAttributes);
  };

  const renderTenantTitleDropdown = (tenantNode: SubscriptionNode, filteredTenantIndex: number): JSX.Element => {
    return (
      <SingleTenantContainer>
        <TenantTextIconContainer>
          <TenantTextStyled>{tenantNode.name}</TenantTextStyled>
          {tenantNode.isDropdownExpand && (
            <Icon
              data-testid="contract-attribute-subscriptions"
              aria-label="Contract Attribute Subscriptions"
              iconName="ChevronUp"
              className={dropdownIconClass}
              onClick={() => onChangeDropdown(tenantNode, filteredTenantIndex)}
            />
          )}
          {!tenantNode.isDropdownExpand && (
            <Icon
              data-testid="expand-attribute-subscriptions"
              aria-label="Expand Attribute Subscriptions"
              iconName="ChevronDown"
              className={dropdownIconClass}
              onClick={() => onChangeDropdown(tenantNode, filteredTenantIndex)}
            />
          )}
        </TenantTextIconContainer>
        <TenantDivider />
      </SingleTenantContainer>
    );
  };

  const renderApprovedAttribute = (attribute: SubscriptionNode): JSX.Element => {
    return (
      <SingleAttributeContainer key={attribute.pathFromRoot}>
        <Icon aria-label="Attribute Icon" iconName="Completed" className={completedCheckIconClass} />
        <Text>{attribute.name}</Text>
      </SingleAttributeContainer>
    );
  };

  return (
    <AttributeSubscriptionsContainer>
      {!isApprovedAttributesLoading &&
        filteredApprovedAttributes.map((tenantNode: SubscriptionNode, filteredTenantIndex: number) => (
          <div key={tenantNode.pathFromRoot}>
            {renderTenantTitleDropdown(tenantNode, filteredTenantIndex)}
            {tenantNode.isDropdownExpand && (
              <MultipleCdmContainer>
                {tenantNode.children.map((cdmNode: SubscriptionNode) => (
                  <div key={cdmNode.pathFromRoot}>
                    <CdmTextStyled>{cdmNode.name}</CdmTextStyled>
                    <MultipleAttributeContainer>
                      {cdmNode.children.map((attributeNode: SubscriptionNode) => renderApprovedAttribute(attributeNode))}
                    </MultipleAttributeContainer>
                  </div>
                ))}
              </MultipleCdmContainer>
            )}
          </div>
        ))}

      {isApprovedAttributesLoading && <Spinner size={SpinnerSize.large} />}
    </AttributeSubscriptionsContainer>
  );
}

const AttributeSubscriptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 32px;
  padding-top: 32px;
  padding-bottom: 16px;
  padding-left: 24px;
  padding-right: 24px;
`;

const SingleTenantContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  align-items: space-between;
`;

const TenantTextIconContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 548px;
`;

const TenantDivider = styled.div`
  border: 1px solid #e5e5e5;
  width: 548px;
`;

const TenantTextStyled = styled(Text)`
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  color: #323130;
`;

const MultipleCdmContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 32px;
  padding-top: 32px;
  padding-bottom: 32px;
`;

const CdmTextStyled = styled(Text)`
  display: flex;
  font-weight: 600;
  font-size: 14px;
  line-height: 14px;
  box-shadow: #171717;
  padding-bottom: 24px;
`;

const MultipleAttributeContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
`;

const SingleAttributeContainer = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 16px;
`;
